import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, db } from '../firebase.config';
import { auth } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { sendPasswordResetEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification, reload } from 'firebase/auth';
// Device management excluded from production deployment
// import deviceManager, { listUserDevices, registerCurrentDevice, removeDevice, getOrCreateDeviceId } from '../utils/deviceManager';
import { getSettingsDoc, getSettingsSubDoc, APP_ID } from '../utils/appArtifacts';
import { getShareDomain } from '../utils/domainConfig';
import VerificationDebug from './VerificationDebug';
import { Mail, CheckCircle, AlertCircle, Send, RefreshCw, Shield, Clock, Building2, User, Key, Fingerprint, Phone } from 'lucide-react';

const Settings = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('security');
  const [mpin, setMpin] = useState('');
  const [confirmMpin, setConfirmMpin] = useState('');
  const [mpinError, setMpinError] = useState('');
  const [mpinSuccess, setMpinSuccess] = useState('');
  const [isMpinSet, setIsMpinSet] = useState(false);
  
  // Account summary
  const [account, setAccount] = useState({ uid:'', email:'', displayName:'', emailVerified:false, phoneNumber:'', providers:[], creationTime:'', lastSignInTime:'', photoURL:'' });
  const [company, setCompany] = useState(null);
  
  // MPIN change security states
  const [showCurrentMpinModal, setShowCurrentMpinModal] = useState(false);
  const [currentMpin, setCurrentMpin] = useState('');
  const [currentMpinError, setCurrentMpinError] = useState('');
  const [showForgotMpinModal, setShowForgotMpinModal] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState(''); // 'email', 'mobile', 'google'
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Profile
  const [profile, setProfile] = useState({ displayName: '', email: '', phone: '', recoveryEmail: '', secondaryPhone: '', language: 'en-IN', timezone: 'Asia/Kolkata', currency: 'INR' });
  const [profileMsg, setProfileMsg] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  // Devices
  const [devices, setDevices] = useState([]);

  // Payments
  const [pg, setPg] = useState({ provider: 'razorpay', mode: 'test', keyId: '', status: 'not_configured' });
  const [pgMsg, setPgMsg] = useState('');
  // SMS
  const [sms, setSms] = useState({ provider: 'msg91', senderId: '', defaultTemplate: '', status: 'not_configured' });
  const [smsMsg, setSmsMsg] = useState('');
  // Branding
  const [brand, setBrand] = useState({ shareMessageDefault: '', footerText: 'Acctoo.com Simplifying Accounting, Together' });
  const [brandMsg, setBrandMsg] = useState('');
  // Password ops
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  // Email verification
  const [emailVerificationMsg, setEmailVerificationMsg] = useState('');
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [verificationCountdown, setVerificationCountdown] = useState(0);

  /** Advanced: show orphan wallet scan/repair on Employee Cash Flow report (default off) */
  const [showEmployeeCashFlowOrphanTools, setShowEmployeeCashFlowOrphanTools] = useState(false);
  const [orphanToolsSaving, setOrphanToolsSaving] = useState(false);
  const [orphanToolsMsg, setOrphanToolsMsg] = useState('');

  useEffect(() => {
    loadUserSettings();
  }, []);

  // Countdown timer for email verification resend - also check persistent rate limit
  useEffect(() => {
    // Check persistent rate limit on mount
    const user = auth.currentUser;
    if (user?.uid) {
      const rateLimitKey = `email_verification_rate_limit_${user.uid}`;
      const lastSentTime = localStorage.getItem(rateLimitKey);
      if (lastSentTime) {
        const now = Date.now();
        const elapsed = now - parseInt(lastSentTime);
        const RATE_LIMIT_MS = 60000; // 60 seconds
        if (elapsed < RATE_LIMIT_MS) {
          const remainingSeconds = Math.ceil((RATE_LIMIT_MS - elapsed) / 1000);
          setVerificationCountdown(remainingSeconds);
        }
      }
    }
    
    let timer;
    if (verificationCountdown > 0) {
      timer = setTimeout(() => setVerificationCountdown(verificationCountdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [verificationCountdown]);

  useEffect(() => {
    const updateAccount = () => {
      const u = auth.currentUser;
      if (!u) return;
      setAccount({
        uid: u.uid,
        email: u.email || '',
        displayName: u.displayName || '',
        emailVerified: !!u.emailVerified,
        phoneNumber: u.phoneNumber || '',
        providers: (u.providerData || []).map(p => p.providerId),
        creationTime: u.metadata?.creationTime || '',
        lastSignInTime: u.metadata?.lastSignInTime || '',
        photoURL: u.photoURL || ''
      });
      // Ensure primary email is always from login
      setProfile(prev => ({ ...prev, email: u.email || '' }));
    };
    
    updateAccount();
    
    // Listen for auth state changes to update email verification status
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setAccount(prev => ({
          ...prev,
          emailVerified: !!u.emailVerified,
          email: u.email || prev.email
        }));
      }
    });
    
    // Fetch company details if available
    try {
      const u = auth.currentUser;
      if (u) {
        const ref = doc(getSettingsDoc(u.uid).parent.parent, u.uid, 'companyDetails', 'myCompany');
        getDoc(ref).then(snap => {
          if (snap.exists()) setCompany(snap.data());
        }).catch(()=>{});
      }
    } catch {}
    
    return () => unsubscribe();
  }, []);

  const loadUserSettings = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const settingsRef = getSettingsDoc(userId);
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const userData = settingsSnap.data();
        setIsMpinSet(!!userData.mpin);
        setProfile(prev => ({
          ...prev,
          displayName: userData.displayName || auth.currentUser?.displayName || '',
          email: auth.currentUser?.email || '',
          phone: userData.phone || '',
          recoveryEmail: userData.recoveryEmail || userData.secondaryEmail || '',
          secondaryPhone: userData.secondaryPhone || '',
          language: userData.language || 'en-IN',
          timezone: userData.timezone || 'Asia/Kolkata',
          currency: userData.currency || 'INR',
        }));
        setShowEmployeeCashFlowOrphanTools(!!userData.showEmployeeCashFlowOrphanTools);
      } else {
        setProfile(prev => ({ ...prev, email: auth.currentUser?.email || '' }));
        setShowEmployeeCashFlowOrphanTools(false);
      }


      try {
        const pgSnap = await getDoc(getSettingsSubDoc(userId, 'Payments', 'public'));
        if (pgSnap.exists()) setPg(prev => ({ ...prev, ...pgSnap.data() }));
      } catch {}
      try {
        const smsSnap = await getDoc(getSettingsSubDoc(userId, 'SMS', 'public'));
        if (smsSnap.exists()) setSms(prev => ({ ...prev, ...smsSnap.data() }));
      } catch {}
      try {
        const brandSnap = await getDoc(getSettingsSubDoc(userId, 'Branding', 'default'));
        if (brandSnap.exists()) setBrand(prev => ({ ...prev, ...brandSnap.data() }));
      } catch {}

      // Device management excluded from production deployment
      // Ensure current device registration (do not exceed 3)
      // const res = await registerCurrentDevice(userId, { platform: 'web' });
      // if (!res.ok && res.reason === 'limit') {
      //   // user can remove from UI
      // }
      // const list = await listUserDevices(userId);
      // setDevices(list);

    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  const handleSaveEmployeeCashFlowOrphanToggle = async (checked) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    setOrphanToolsSaving(true);
    setOrphanToolsMsg('');
    const prev = showEmployeeCashFlowOrphanTools;
    setShowEmployeeCashFlowOrphanTools(checked);
    try {
      await setDoc(
        getSettingsDoc(userId),
        { showEmployeeCashFlowOrphanTools: checked, updatedAt: new Date() },
        { merge: true }
      );
      setOrphanToolsMsg('Saved');
      setTimeout(() => setOrphanToolsMsg(''), 2500);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('acctoo-user-settings-changed'));
      }
    } catch (e) {
      setShowEmployeeCashFlowOrphanTools(prev);
      setOrphanToolsMsg(e.message || 'Could not save');
    } finally {
      setOrphanToolsSaving(false);
    }
  };

  const handleProfileSave = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      const settingsRef = getSettingsDoc(userId);
      await setDoc(settingsRef, {
        displayName: profile.displayName,
        phone: profile.phone,
        recoveryEmail: profile.recoveryEmail,
        secondaryEmail: profile.recoveryEmail, // backward compatibility
        secondaryPhone: profile.secondaryPhone,
        language: profile.language,
        timezone: profile.timezone,
        currency: profile.currency,
        updatedAt: new Date(),
        email: auth.currentUser?.email || profile.email,
      }, { merge: true });
      setProfileMsg('Saved');
      setIsEditingProfile(false);
      setTimeout(()=>setProfileMsg(''), 1500);
    } catch (e) {
      setProfileMsg(e.message || 'Failed');
    }
  };

  const handleProfileCancel = async () => {
    setIsEditingProfile(false);
    await loadUserSettings();
  };

  // Device management functions excluded from production deployment
  // const refreshDevices = async () => {
  //   const userId = auth.currentUser?.uid;
  //   if (!userId) return;
  //   const list = await listUserDevices(userId);
  //   setDevices(list);
  // }

  // const handleRemoveDevice = async (deviceId) => {
  //   const userId = auth.currentUser?.uid;
  //   if (!userId) return;
  //   await removeDevice(userId, deviceId);
  //   await refreshDevices();
  // }

  const handleForgotPassword = async () => {
    try {
      setPwdMsg(''); setPwdError('');
      const email = auth.currentUser?.email;
      if (!email) throw new Error('No login email found');
      
      const actionCodeSettings = {
        url: `${getShareDomain()}/#/login`,
        handleCodeInApp: false,
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setPwdMsg('Password reset link sent to your email.');
    } catch (e) {
      setPwdError(e.message || 'Failed to send reset email');
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      setEmailVerificationMsg('');
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user found. Please login again.');
      }
      
      if (!user.email) {
        throw new Error('No email address found for this account.');
      }
      
      // Check persistent rate limiting (survives page refresh)
      const rateLimitKey = `email_verification_rate_limit_${user.uid}`;
      const lastSentTime = localStorage.getItem(rateLimitKey);
      const now = Date.now();
      const RATE_LIMIT_MS = 60000; // 60 seconds minimum between requests
      
      if (lastSentTime && (now - parseInt(lastSentTime)) < RATE_LIMIT_MS) {
        const remainingSeconds = Math.ceil((RATE_LIMIT_MS - (now - parseInt(lastSentTime))) / 1000);
        setEmailVerificationMsg(`❌ Please wait ${remainingSeconds} seconds before requesting another verification email.`);
        setVerificationCountdown(remainingSeconds);
        return;
      }
      
      // Prevent multiple simultaneous calls
      if (isSendingVerification) {
        setEmailVerificationMsg('❌ Verification email is already being sent. Please wait...');
        return;
      }
      
      setIsSendingVerification(true);
      
      const actionCodeSettings = {
        url: `${getShareDomain()}/#/login`,
        handleCodeInApp: false,
      };
      
      await sendEmailVerification(user, actionCodeSettings);
      
      // Store timestamp in localStorage for persistent rate limiting
      localStorage.setItem(rateLimitKey, now.toString());
      
      setEmailVerificationMsg('✅ Verification email sent! Please check your inbox and click the verification link.');
      setVerificationCountdown(60); // 60 second countdown before allowing resend
    } catch (error) {
      console.error('Error sending email verification:', error);
      
      if (error.code === 'auth/too-many-requests') {
        // Firebase's server-side rate limit - store timestamp to prevent retries
        const user = auth.currentUser;
        if (user) {
          const rateLimitKey = `email_verification_rate_limit_${user.uid}`;
          localStorage.setItem(rateLimitKey, Date.now().toString());
        }
        setEmailVerificationMsg('❌ Too many requests. Please wait at least 60 seconds before trying again.');
        setVerificationCountdown(60);
      } else if (error.code === 'auth/network-request-failed') {
        setEmailVerificationMsg('❌ Network error. Please check your internet connection.');
      } else {
        setEmailVerificationMsg(`❌ Failed to send verification email: ${error.message}`);
      }
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleCheckEmailVerification = async () => {
    try {
      setEmailVerificationMsg('');
      const user = auth.currentUser;
      if (!user) {
        setEmailVerificationMsg('❌ No user found. Please login again.');
        return;
      }
      
      // Reload user to get latest emailVerified status
      await reload(user);
      
      // Update Firestore user document with latest emailVerified status
      if (user.emailVerified && db) {
        try {
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            emailVerified: true,
            updatedAt: serverTimestamp()
          }, { merge: true });
          console.log('✅ User document updated with emailVerified status in Settings');
        } catch (firestoreError) {
          console.error('⚠️ Could not update Firestore user document:', firestoreError);
          // Continue anyway - token is already updated
        }
      }
      
      // Update account state
      setAccount(prev => ({
        ...prev,
        emailVerified: !!user.emailVerified
      }));
      
      if (user.emailVerified) {
        setEmailVerificationMsg('✅ Email verified successfully!');
      } else {
        setEmailVerificationMsg('❌ Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
      setEmailVerificationMsg('❌ Error checking verification status. Please try again.');
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdMsg(''); setPwdError('');
    try {
      if (!newPassword || newPassword.length < 6) throw new Error('New password must be at least 6 characters');
      if (newPassword !== confirmNewPassword) throw new Error('Passwords do not match');
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('User not authenticated');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPwdMsg('Password updated successfully.');
      setShowChangePwd(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (e) {
      setPwdError(e.message || 'Failed to update password');
    }
  };



  const savePayments = async () => {
    try {
      const userId = auth.currentUser?.uid; if (!userId) return;
      const pgRef = getSettingsSubDoc(userId, 'Payments', 'public');
      await setDoc(pgRef, { provider: pg.provider, mode: pg.mode, keyId: pg.keyId, status: 'configured', updatedAt: new Date() }, { merge: true });
      setPgMsg('Saved'); setTimeout(()=>setPgMsg(''), 1500);
    } catch (e) { setPgMsg(e.message || 'Failed'); }
  };

  const saveSMS = async () => {
    try {
      const userId = auth.currentUser?.uid; if (!userId) return;
      const smsRef = getSettingsSubDoc(userId, 'SMS', 'public');
      await setDoc(smsRef, { provider: sms.provider, senderId: sms.senderId, defaultTemplate: sms.defaultTemplate, status: 'configured', updatedAt: new Date() }, { merge: true });
      setSmsMsg('Saved'); setTimeout(()=>setSmsMsg(''), 1500);
    } catch (e) { setSmsMsg(e.message || 'Failed'); }
  };

  const saveBranding = async () => {
    try {
      const userId = auth.currentUser?.uid; if (!userId) return;
      const brandRef = getSettingsSubDoc(userId, 'Branding', 'default');
      await setDoc(brandRef, { ...brand, updatedAt: new Date() }, { merge: true });
      setBrandMsg('Saved'); setTimeout(()=>setBrandMsg(''), 1500);
    } catch (e) { setBrandMsg(e.message || 'Failed'); }
  };

  const handleMpinSubmit = async (e) => {
    e.preventDefault();
    setMpinError('');
    setMpinSuccess('');

    const normalized = (mpin || '').toString().trim().toUpperCase();
    if (normalized.length !== 4) {
      setMpinError('MPIN must be exactly 4 characters');
      return;
    }

    if (!/^[A-Z0-9]{4}$/.test(normalized)) {
      setMpinError('MPIN must be alphanumeric (A-Z, 0-9)');
      return;
    }

    if (normalized !== (confirmMpin || '').toString().trim().toUpperCase()) {
      setMpinError('MPIN and confirmation do not match');
      return;
    }

    if (isMpinSet) {
      setShowCurrentMpinModal(true);
      return;
    }

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setMpinError('User not authenticated');
        return;
      }

      const hashedMpin = btoa(normalized); // demo only (replace with proper KDF in production)
      const settingsRef = getSettingsDoc(userId);
      const userDoc = await getDoc(settingsRef);
      if (userDoc.exists()) {
        await updateDoc(settingsRef, { mpin: hashedMpin, mpinSetAt: new Date() });
      } else {
        await setDoc(settingsRef, { mpin: hashedMpin, mpinSetAt: new Date(), email: auth.currentUser.email, createdAt: new Date() }, { merge: true });
      }

      setMpinSuccess('MPIN set successfully!');
      setIsMpinSet(true);
      setMpin('');
      setConfirmMpin('');
      setTimeout(() => setMpinSuccess(''), 3000);
    } catch (error) {
      console.error('Error setting MPIN:', error);
      setMpinError(`Failed to set MPIN: ${error.message}`);
    }
  };

  const handleCurrentMpinSubmit = async (e) => {
    e.preventDefault();
    setCurrentMpinError('');

    const normalizedCurrent = (currentMpin || '').toString().trim().toUpperCase();
    if (normalizedCurrent.length !== 4 || !/^[A-Z0-9]{4}$/.test(normalizedCurrent)) {
      setCurrentMpinError('Please enter your current 4-character MPIN (A-Z, 0-9)');
      return;
    }

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setCurrentMpinError('User not authenticated');
        return;
      }

      const userDoc = await getDoc(getSettingsDoc(userId));
      if (!userDoc.exists()) {
        setCurrentMpinError('User data not found');
        return;
      }

      const userData = userDoc.data();
      const storedMpin = userData.mpin;
      if (!storedMpin) {
        setCurrentMpinError('No MPIN found. Please set a new MPIN.');
        return;
      }

      const hashedInput = btoa(normalizedCurrent);
      if (hashedInput === storedMpin) {
        setShowCurrentMpinModal(false);
        setCurrentMpin('');
        setCurrentMpinError('');
        await setNewMpin();
      } else {
        setCurrentMpinError('Incorrect current MPIN');
        setCurrentMpin('');
      }
    } catch (error) {
      console.error('Error verifying current MPIN:', error);
      setCurrentMpinError('Error verifying MPIN. Please try again.');
    }
  };

  const handleForgotMpin = () => {
    setShowCurrentMpinModal(false);
    setShowForgotMpinModal(true);
    setVerificationMethod('');
    setVerificationCode('');
    setVerificationError('');
  };

  const handleSendVerificationCode = async (method) => {
    setVerificationMethod(method);
    setIsVerifying(true);
    setVerificationError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Verification code sent. Demo code: 123456');
    } catch (error) {
      console.error('Error sending verification code:', error);
      setVerificationError('Failed to send verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerificationError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Please enter the 6-digit verification code');
      return;
    }

    try {
      if (verificationCode === '123456') {
        setShowForgotMpinModal(false);
        setVerificationCode('');
        setVerificationError('');
        await setNewMpin();
      } else {
        setVerificationError('Incorrect verification code');
        setVerificationCode('');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setVerificationError('Error verifying code. Please try again.');
    }
  };

  const setNewMpin = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setMpinError('User not authenticated');
        return;
      }

      const normalized = (mpin || '').toString().trim().toUpperCase();
      if (!/^[A-Z0-9]{4}$/.test(normalized)) {
        setMpinError('MPIN must be 4 characters (A-Z, 0-9)');
        return;
      }
      const hashedMpin = btoa(normalized);
      const userDocRef = getSettingsDoc(userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        await updateDoc(userDocRef, { mpin: hashedMpin, mpinSetAt: new Date() });
      } else {
        await setDoc(userDocRef, { mpin: hashedMpin, mpinSetAt: new Date(), email: auth.currentUser.email, createdAt: new Date() });
      }

      setMpinSuccess('MPIN set successfully!');
      setIsMpinSet(true);
      setMpin('');
      setConfirmMpin('');
      setTimeout(() => setMpinSuccess(''), 3000);
    } catch (error) {
      console.error('Error setting MPIN:', error);
      setMpinError(`Failed to set MPIN: ${error.message}`);
    }
  };

  const hasPhoneAuth =
    (account.providers || []).includes('phone') && !!account.phoneNumber;
  const isAccountVerified = account.emailVerified || hasPhoneAuth;
  const needsEmailLink = hasPhoneAuth && !account.email;

  return (
    <div className="max-w-5xl mx-auto px-2 md:px-0">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('settings')}</h2>
      </div>

      {/* Account summary */}
      <div className="mb-4 bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Gradient header */}
        <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
        
        <div className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Avatar */}
            <div className="relative mx-auto sm:mx-0">
              <div className="w-16 h-16 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-lg">
                {account.photoURL ? (
                  <img src={account.photoURL} alt="avatar" className="w-16 h-16 sm:w-14 sm:h-14 object-cover" />
                ) : (
                  <span className="text-2xl sm:text-xl font-bold text-white">{(account.displayName || account.email || 'U').charAt(0).toUpperCase()}</span>
                )}
              </div>
              {/* Verification badge */}
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                isAccountVerified
                  ? 'bg-green-500 text-white' 
                  : 'bg-amber-500 text-white'
              } ring-2 ring-white`}>
                {isAccountVerified
                  ? <CheckCircle className="w-3.5 h-3.5" />
                  : <AlertCircle className="w-3.5 h-3.5" />
                }
              </div>
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              {/* Name and status */}
              <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap mb-1">
                <h3 className="text-lg font-bold text-gray-900">{account.displayName || 'User'}</h3>
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${
                  isAccountVerified
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {account.emailVerified ? (
                    <><CheckCircle className="w-3 h-3" /> Verified</>
                  ) : hasPhoneAuth ? (
                    <><Phone className="w-3 h-3" /> Phone verified</>
                  ) : (
                    <><AlertCircle className="w-3 h-3" /> Unverified</>
                  )}
                </span>
              </div>
              
              {/* Email */}
              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-sm text-gray-600 mb-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="break-all">{account.email || '—'}</span>
              </div>

              {needsEmailLink && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 mb-3 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">Add an email address</span>
                  </div>
                  <p className="text-xs text-blue-800 mb-3 leading-relaxed">
                    Your phone is verified. Link an email and password under Login &amp; Security for account recovery,
                    invoices, and email verification.
                  </p>
                  <Link
                    to="/login-settings"
                    className="inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold"
                  >
                    Link email in Login &amp; Security
                  </Link>
                </div>
              )}
              
              {/* Email verification section */}
              {!account.emailVerified && account.email && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200 mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">Verify Your Email</span>
                  </div>
                  
                  {emailVerificationMsg && (
                    <div className={`text-xs p-2.5 rounded-lg mb-3 flex items-start gap-2 ${
                      emailVerificationMsg.includes('✅') 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : emailVerificationMsg.includes('❌')
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }`}>
                      {emailVerificationMsg.includes('✅') && <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                      {emailVerificationMsg.includes('❌') && <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                      <span>{emailVerificationMsg.replace(/✅|❌/g, '').trim()}</span>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleSendEmailVerification}
                      disabled={isSendingVerification || verificationCountdown > 0}
                      className="flex-1 flex items-center justify-center gap-2 text-sm px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all touch-manipulation min-h-[48px] font-semibold shadow-md"
                    >
                      {isSendingVerification ? (
                        <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</>
                      ) : verificationCountdown > 0 ? (
                        <><Clock className="w-4 h-4" /> Resend in {verificationCountdown}s</>
                      ) : (
                        <><Send className="w-4 h-4" /> Send Verification</>
                      )}
                    </button>
                    <button
                      onClick={handleCheckEmailVerification}
                      className="flex-1 flex items-center justify-center gap-2 text-sm px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 active:from-green-800 active:to-emerald-800 transition-all touch-manipulation min-h-[48px] font-semibold shadow-md"
                    >
                      <CheckCircle className="w-4 h-4" /> Check Status
                    </button>
                  </div>
                  
                  <p className="text-xs text-amber-700 mt-3 leading-relaxed">
                    Click "Send Verification" to receive a link. After clicking the link in your email, come back and click "Check Status".
                  </p>
                </div>
              )}
              
              {/* Account info cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                  <Key className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <div className="text-[10px] text-gray-500 font-medium">Providers</div>
                  <div className="text-xs text-gray-700 font-semibold truncate">{account.providers.join(', ') || '—'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                  <Clock className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <div className="text-[10px] text-gray-500 font-medium">Last Sign-in</div>
                  <div className="text-xs text-gray-700 font-semibold truncate">{account.lastSignInTime ? new Date(account.lastSignInTime).toLocaleDateString() : '—'}</div>
                </div>
                {company?.firmName && (
                  <div className="bg-blue-50 rounded-lg p-2.5 text-center col-span-2 sm:col-span-2">
                    <Building2 className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                    <div className="text-[10px] text-blue-600 font-medium">Company</div>
                    <div className="text-xs text-blue-700 font-semibold truncate">{company.firmName}{company.city ? `, ${company.city}` : ''}</div>
                  </div>
                )}
              </div>
              
              <div className="text-[10px] text-gray-400 mt-3 flex items-center gap-1">
                <Fingerprint className="w-3 h-3" />
                UID: {account.uid || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Quick Settings</h3>
          <p className="text-sm text-gray-600 mb-4">
            Security and MPIN are under Login & Security. Use the shortcuts below on mobile.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <Link
              to="/login-settings"
              className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors touch-manipulation"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-semibold text-gray-900">Login & Security</div>
                <div className="text-xs text-gray-500 truncate">MPIN, password, verification</div>
              </div>
            </Link>

            <Link
              to="/user-profile"
              className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors touch-manipulation"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                <Key className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-semibold text-gray-900">My Profile</div>
                <div className="text-xs text-gray-500 truncate">Name, contact, preferences</div>
              </div>
            </Link>

            {Capacitor.isNativePlatform() && (
              <Link
                to="/mobile-settings"
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors touch-manipulation"
              >
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <Fingerprint className="w-5 h-5 text-green-600" />
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-semibold text-gray-900">Mobile Settings</div>
                  <div className="text-xs text-gray-500 truncate">Biometrics, permissions, sync</div>
                </div>
              </Link>
            )}

            <Link
              to="/subscription"
              className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors touch-manipulation"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-semibold text-gray-900">Subscription & Usage</div>
                <div className="text-xs text-gray-500 truncate">Plan, limits, upgrades</div>
              </div>
            </Link>
          </div>

          {false && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('securityAndMpin')}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('setFourDigitMpin')}
                </p>
              </div>

              {/* Password controls */}
              <div className="rounded border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-gray-900">Password</div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowChangePwd(!showChangePwd)} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Change Password</button>
                    <button onClick={handleForgotPassword} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Forgot Password</button>
                  </div>
                </div>
                {showChangePwd && (
                  <form onSubmit={handleChangePasswordSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="password" className="px-3 py-2 border rounded" placeholder="Current password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} />
                    <input type="password" className="px-3 py-2 border rounded" placeholder="New password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
                    <input type="password" className="px-3 py-2 border rounded" placeholder="Confirm new password" value={confirmNewPassword} onChange={e=>setConfirmNewPassword(e.target.value)} />
                    <div className="md:col-span-3 flex gap-2">
                      <button type="submit" className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">Update</button>
                      <button type="button" onClick={() => { setShowChangePwd(false); setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword(''); }} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
                    </div>
                  </form>
                )}
                {(pwdMsg || pwdError) && (
                  <div className={`text-sm mt-2 ${pwdError ? 'text-red-600' : 'text-green-600'}`}>{pwdError || pwdMsg}</div>
                )}
              </div>

              {isMpinSet && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-800 font-medium">{t('mpinAlreadySet')}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleMpinSubmit} className="space-y-4">
                <div>
                  <label htmlFor="mpin" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('enterFourDigitMpin')}
                  </label>
                  <input
                    type="password"
                    id="mpin"
                    value={mpin}
                    onChange={(e) => setMpin(e.target.value)}
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter 4 digits"
                  />
                </div>

                <div>
                  <label htmlFor="confirmMpin" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('confirmMpin')}
                  </label>
                  <input
                    type="password"
                    id="confirmMpin"
                    value={confirmMpin}
                    onChange={(e) => setConfirmMpin(e.target.value)}
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm 4 digits"
                  />
                </div>

                {mpinError && (
                  <div className="text-red-600 text-sm">{mpinError}</div>
                )}

                {mpinSuccess && (
                  <div className="text-green-600 text-sm">{mpinSuccess}</div>
                )}

                <button type="submit" className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">{t('setMpin')}</button>
              </form>

              {/* Device Management Section - EXCLUDED FROM PRODUCTION DEPLOYMENT */}
              {/*
              <div className="pt-6 border-t">
                <h4 className="text-md font-semibold mb-2">Devices (max 3)</h4>
                {devices.length === 0 ? (
                  <div className="text-sm text-gray-600">No devices registered.</div>
                ) : (
                  <ul className="divide-y">
                    {devices.map(d => (
                      <li key={d.id} className="py-2 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">{d.platform || 'web'} — {d.id === getOrCreateDeviceId() ? 'This device' : d.id}</div>
                          <div className="text-xs text-gray-500">Last seen: {d.lastSeen?.toDate ? d.lastSeen.toDate().toLocaleString() : (d.lastSeen || '')}</div>
                        </div>
                        <button onClick={()=>handleRemoveDevice(d.id)} className="text-red-600 text-sm hover:underline">Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
                {devices.length >= 3 && (
                  <div className="text-xs text-red-600 mt-2">Device limit reached. Remove a device to add a new one.</div>
                )}
              </div>
              */}
            </div>
          )}




        </div>
      </div>

      {/* Advanced: optional Employee Cash Flow orphan repair (off by default) */}
      <div className="mt-4 bg-white rounded-lg shadow overflow-hidden border border-dashed border-gray-200">
        <div className="p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Advanced</h3>
          <p className="text-xs text-gray-500 mb-4">Optional tools. Disabled by default; enable only if you need them.</p>
          <label className={`flex items-start gap-3 ${orphanToolsSaving ? 'opacity-60' : ''}`}>
            <input
              type="checkbox"
              className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={showEmployeeCashFlowOrphanTools}
              onChange={(e) => handleSaveEmployeeCashFlowOrphanToggle(e.target.checked)}
              disabled={orphanToolsSaving}
            />
            <span>
              <span className="text-sm font-medium text-gray-800">Show orphaned wallet repair on Employee Cash Flow report</span>
              <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                When enabled, Reports → Employee Cash/UPI Flow shows scan and fix tools for wallet rows whose source payment or salary was deleted elsewhere.
              </span>
            </span>
          </label>
          {orphanToolsMsg && (
            <p className={`text-xs mt-2 ${orphanToolsMsg === 'Saved' ? 'text-green-600' : 'text-red-600'}`}>{orphanToolsMsg}</p>
          )}
        </div>
      </div>

      {/* Current MPIN Verification Modal */}
      {false && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('verifyCurrentMpin')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('enterCurrentMpinToChange')}
              </p>
              
              <form onSubmit={handleCurrentMpinSubmit} className="space-y-4">
                <div>
                  <input
                    type="password"
                    value={currentMpin}
                    onChange={(e) => setCurrentMpin(e.target.value)}
                    maxLength={4}
                    className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••"
                    autoFocus
                  />
                </div>
                
                {currentMpinError && (
                  <div className="text-red-600 text-sm">{currentMpinError}</div>
                )}
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCurrentMpinModal(false);
                      setCurrentMpin('');
                      setCurrentMpinError('');
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Verify
                  </button>
                </div>
              </form>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={handleForgotMpin}
                  className="text-blue-600 text-sm hover:text-blue-700 underline"
                >
                  {t('forgotMpin')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot MPIN Modal */}
      {showForgotMpinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('forgotMpin')}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('chooseVerificationMethod')}
              </p>
              
              {!verificationMethod ? (
                <div className="space-y-3">
                  <button
                    onClick={() => handleSendVerificationCode('email')}
                    disabled={isVerifying}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isVerifying ? 'Sending...' : '📧 Send Code via Email'}
                  </button>
                  <button
                    onClick={() => handleSendVerificationCode('mobile')}
                    disabled={isVerifying}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isVerifying ? 'Sending...' : '📱 Send Code via SMS'}
                  </button>
                  <button
                    onClick={() => handleSendVerificationCode('google')}
                    disabled={isVerifying}
                    className="w-full bg-red-600 text-white py-3 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {isVerifying ? 'Verifying...' : '🔐 Verify with Google'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowForgotMpinModal(false);
                      setVerificationMethod('');
                      setVerificationCode('');
                      setVerificationError('');
                    }}
                    className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      {t('enterSixDigitCode').replace('{verificationMethod}', verificationMethod)}
                    </p>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      maxLength={6}
                      className="w-full px-4 py-3 text-center text-xl font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123456"
                      autoFocus
                    />
                  </div>
                  
                  {verificationError && (
                    <div className="text-red-600 text-sm">{verificationError}</div>
                  )}
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setVerificationMethod('');
                        setVerificationCode('');
                        setVerificationError('');
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      {t('back')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {t('verifyCode')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 